const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// The 24h session token is kept in the Secure cookie AND localStorage, so embedded/iframe browsers that drop third-party cookies still stay signed in.
const TOKEN_KEY = "jarvis_owner_session";
let sessionGeneration = 0;
let sessionListener = null;
export function changeAuthSession() { sessionGeneration += 1; }
export function clearStoredSession() { try { localStorage.removeItem(TOKEN_KEY); } catch { /* storage blocked */ } }
function storeSession(payload) {
  if (!payload?.access_token) return;
  try { localStorage.setItem(TOKEN_KEY, payload.access_token); } catch { /* storage blocked */ }
}
function storedToken() { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } }
export function onSessionExpired(listener) { sessionListener = listener; return () => { sessionListener = null; }; }
export function authHeaders() {
  const token = storedToken();
  return { "X-Jarvis-Client": "web", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function checked(response, authenticated = true, requestToken) {
  if (response.ok) return response;
  // An old request must not erase a newer session or a private replacement QR.
  if (response.status === 401 && authenticated && requestToken === sessionGeneration) { clearStoredSession(); changeAuthSession(); sessionListener?.(); }
  const payload = await response.json().catch(() => ({}));
  const error = new Error(typeof payload.detail === "string" ? payload.detail : `Request failed (${response.status})`);
  error.status = response.status;
  throw error;
}

export async function authRequest(path, body, bootstrapKey, signal, enrollmentToken) {
  const response = await fetch(`${API}${path}`, {
    method: body === undefined ? "GET" : "POST", signal, cache: "no-store", credentials: "include",
    headers: { ...authHeaders(), "Content-Type": "application/json", ...(bootstrapKey ? { "X-Bootstrap-Key": bootstrapKey } : {}), ...(enrollmentToken ? { "X-Enrollment-Token": enrollmentToken } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await (await checked(response, false)).json();
  storeSession(payload);
  return payload;
}

async function request(path, method, body, multipart = false, expireSession = true) {
  const token = sessionGeneration;
  const response = await fetch(`${API}${path}`, { method, cache: "no-store", credentials: "include",
    headers: { ...authHeaders(), ...(!multipart && body !== undefined ? { "Content-Type": "application/json" } : {}) },
    body: body === undefined ? undefined : multipart ? body : JSON.stringify(body),
  });
  const payload = await (await checked(response, expireSession, token)).json();
  storeSession(payload);
  return payload;
}

function recoveryKeyValue(value) {
  const key = value.trim().replace(/^OWNER_RECOVERY_KEY\s*=\s*/, "");
  return /^(["']).*\1$/.test(key) ? key.slice(1, -1) : key;
}

export async function removeOwnerPassword(recoveryKey, code) {
  const response = await fetch(`${API}/auth/password/remove`, {
    method: "POST", cache: "no-store", credentials: "include",
    headers: { ...authHeaders(), "Content-Type": "application/json", "X-Owner-Recovery-Key": recoveryKeyValue(recoveryKey) },
    body: JSON.stringify({ code, confirm: true }),
  });
  const payload = await (await checked(response, false)).json();
  storeSession(payload);
  return payload;
}

export async function recoverOwner(recoveryKey) {
  const response = await fetch(`${API}/auth/recovery`, {
    method: "POST", cache: "no-store", credentials: "include",
    headers: { ...authHeaders(), "Content-Type": "application/json", "X-Owner-Recovery-Key": recoveryKeyValue(recoveryKey) },
    body: JSON.stringify({ confirm: true }),
  });
  const payload = await (await checked(response, false)).json();
  storeSession(payload);
  return payload;
}

export const setupOwnerPassword = (body) => request("/auth/password/setup", "POST", body, false, false);
export const replaceAuthenticator = (body) => request("/auth/authenticator/replace", "POST", body, false, false);
// A wrong JIT code is a 401 on the action, not on the session — never auto-lock for it.
export const vaultReveal = (id, body) => request(`/vault/${id}/reveal`, "POST", body, false, false);
export const apiGet = (path) => request(path, "GET");
export const apiPost = (path, body) => request(path, "POST", body);
export const protectedPost = apiPost;
export const protectedGet = apiGet;
export const protectedUpload = (path, formData) => request(path, "POST", formData, true);
export const protectedDelete = (path, body) => request(path, "DELETE", body);

export async function fileBlob(id, signal) {
  const token = sessionGeneration;
  const response = await fetch(`${API}/files/${encodeURIComponent(id)}/download`, { headers: authHeaders(), cache: "no-store", credentials: "include", signal });
  return (await checked(response, true, token)).blob();
}

export async function downloadFile(file) {
  const url = URL.createObjectURL(await fileBlob(file.id));
  const anchor = document.createElement("a");
  anchor.href = url; anchor.download = file.name; anchor.rel = "noopener";
  document.body.appendChild(anchor); anchor.click(); anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function dispatchChatFrame(raw, handlers) {
  if (!raw.startsWith("data:")) return false;
  const event = JSON.parse(raw.slice(5).trim());
  const payload = { start: event, delta: event.text, done: event, citations: event.urls || [], error: event.message };
  handlers[event.type]?.(payload[event.type]);
  return event.type === "done" || event.type === "error";
}

export async function streamChat({ messages, provider, sessionId, signal, onStart, onDelta, onDone, onError, onCitations }) {
  const token = sessionGeneration;
  const res = await fetch(`${API}/chat/stream`, {
    method: "POST", signal, credentials: "include", cache: "no-store",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ messages, provider, session_id: sessionId }),
  });
  await checked(res, true, token);
  if (!res.body) throw new Error("Chat stream is unavailable");
  const reader = res.body.getReader(), decoder = new TextDecoder();
  const handlers = { start: onStart, delta: onDelta, done: onDone, error: onError, citations: onCitations };
  let buffer = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) throw new Error("Chat stream ended before completion. Please retry.");
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
      const frames = buffer.split("\n\n"); buffer = frames.pop();
      for (const frame of frames) if (dispatchChatFrame(frame, handlers)) return;
    }
  } finally {
    await reader.cancel().catch(() => console.warn("Chat stream was already closed."));
    reader.releaseLock();
  }
}
